import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import { endOfDay, parseISO } from "date-fns";
import { useSnackbar } from "notistack";
import {
  DefaultModalProps,
  ModalSkeleton,
  TextField,
  Checkbox,
  SelectField,
} from "../../../components";
import {
  Absence,
  CreateAbsenceInput,
  Country,
  AbsenceType,
} from "../../../API";
import { User, useUser } from "../../../hooks";
import {
  formatHoursChange,
  getBusinessDayDifference,
  toAWSDate,
} from "../../../lib";
import { absenceTypeOptions } from "../utils";
import { useTimeOffQuery } from "../useTimeOffQuery";
import DateControl from "../../../components/DatePickers/DateControl";
import { useBankHolidayQuery } from "../../BankHolidays/useBankHolidayQuery";
import { useEmployeeTimeInLieusQuery } from "../../TimeInLieu/useTimeInLieuQuery";
import { TimeOffDates } from "./TimeOffDates";
import {
  checkExistingAbsences,
  getDescription,
  mapDefaultValues,
} from "./utils";

const TEST_ID = "request-modal";

export type FormFields = CreateAbsenceInput;
type RangePickerFields = "startDate" | "endDate" | "amount";

type Props = DefaultModalProps & {
  title: string;
  onSubmit: ({ data }: { data: FormFields }) => void;
  testID: string;
  params: {
    row: Absence;
    absences?: Absence[];
    maxAmount: number;
    country: Country;
    employeeID: string;
    maxDate: Date;
    minDate: Date;
  };
};

export const RequestModal = ({
  title,
  onSubmit,
  testID,
  params: { row, maxAmount, absences, country, employeeID, maxDate, minDate },
  ...rest
}: Props) => {
  const user = useUser() as User;
  const { control, handleSubmit, setValue, getValues } = useForm<FormFields>({
    defaultValues: mapDefaultValues(row, user, employeeID, minDate),
  });

  const getPickerDays = useCallback(
    () => ({
      start: endOfDay(parseISO(getValues("startDate"))),
      end: endOfDay(parseISO(getValues("endDate"))),
    }),
    [getValues]
  );

  const [pickerDays, setPickerDays] = useState<Interval>(getPickerDays());
  const [absenceType, setAbsenceType] = useState<
    AbsenceType | undefined | null
  >(getValues("type"));
  const [saving, setSaving] = useState(false);

  const { timeInLieuHoursUsed } = useTimeOffQuery(employeeID, {
    start: maxDate,
    end: minDate,
  });
  const { timeInLieuHours } = useEmployeeTimeInLieusQuery(employeeID, {
    start: new Date(minDate),
    end: new Date(maxDate),
  });
  const { count: amountOfDaysWithinPeriod, items: bankHolidays } =
    useBankHolidayQuery(pickerDays, country);

  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const maxAvailable = row?.amount ? row.amount + maxAmount : maxAmount;

  useEffect(() => {
    const { start, end } = getPickerDays();
    const isAnnualLeave = getValues("type") === AbsenceType.holiday;
    let amountOfDays = getBusinessDayDifference(start, end);

    if (isAnnualLeave) {
      amountOfDays = Math.max(0, amountOfDays - amountOfDaysWithinPeriod);
      const description = getValues("description") ?? "";

      setValue(
        "description",
        getDescription({ amountOfDaysWithinPeriod, description })
      );
    }
    setValue("amount", amountOfDays);
  }, [
    pickerDays,
    amountOfDaysWithinPeriod,
    getPickerDays,
    getValues,
    setValue,
  ]);

  const updateDates = (field: RangePickerFields, date: Date) => {
    setValue(field, date.toISOString());
  };

  const handleDateChange = (
    value: Date | string | null,
    field: RangePickerFields
  ) => {
    if (!value) return;
    const { start, end } = getPickerDays();
    if (
      (field === "startDate" && value > end) ||
      (field === "endDate" && value < start)
    ) {
      updateDates("startDate", value as Date);
      updateDates("endDate", value as Date);

      setPickerDays({ start: value as Date, end: value as Date });
    } else {
      updateDates(field, value as Date);

      if (field === "startDate") setPickerDays({ start: value as Date, end });
      if (field === "endDate") setPickerDays({ end: value as Date, start });
    }
  };

  const handleTimeInLieuDateChange = (value: Date | null) => {
    const newDate = new Date(value ?? "").toISOString();
    if (!newDate) return;
    setValue("startDate", newDate);
    setValue("endDate", newDate);

    const hoursTakenValue = getValues("hoursTaken") ?? 8;
    setValue("hoursTaken", newDate ? hoursTakenValue : 0);
  };

  return (
    <ModalSkeleton
      {...rest}
      title={title}
      isForm
      primaryAction={{
        label: "Save",
        pending: saving,
        onSubmit: handleSubmit((data) => {
          if (!data.approvedByClient) {
            showSnackbar(
              "Time off must by approved by a Client first, before submitting it",
              true
            );
            return;
          }

          if (
            absences &&
            checkExistingAbsences({
              absences,
              formStartDate: data.startDate,
              formEndDate: data.endDate,
              currentAbsenceId: data.id,
            })
          ) {
            showSnackbar("Time off dates already exist in the system", true);
            return;
          }

          if (data.type === AbsenceType.timeInLieu) {
            if (!data.hoursTaken) {
              showSnackbar(
                "Need to specify amount of hours taken from time in lieu",
                true
              );
              return;
            }

            if (
              bankHolidays.find(
                (bh) => bh.date === toAWSDate(new Date(data.startDate))
              )
            ) {
              showSnackbar(
                "Can't take time in lieu off, which is already a holiday",
                true
              );
              return;
            }

            if (
              timeInLieuHours <
              timeInLieuHoursUsed + (data.hoursTaken - (row?.hoursTaken ?? 0))
            ) {
              showSnackbar(
                "There aren't enough time in lieu to request a time off",
                true
              );
              return;
            }

            data.endDate = data.startDate;
          }

          data.hoursTaken =
            data.type === AbsenceType.timeInLieu
              ? data.hoursTaken
              : Number(data.amount) * 8;

          data.amount =
            data.type === AbsenceType.holiday ? Number(data.amount) : 0;

          if (data?.employeeID) {
            setSaving(true);
            return onSubmit({ data } as { data: FormFields });
          }
          return;
        }),
        testID: "save-request",
      }}
      testID={testID}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(3, 1fr)" },
          alignItems: "center",
          gap: 2,
        }}
      >
        <SelectField
          label="Type"
          placeholder="Select type"
          options={absenceTypeOptions}
          name="type"
          control={control}
          onChange={(e) => {
            setValue("type", e.target.value as AbsenceType);
            setAbsenceType(e.target.value as AbsenceType);
          }}
          required
        />

        {absenceType === AbsenceType.timeInLieu && (
          <>
            <DateControl
              label="Select day off"
              control={control}
              name="startDate"
              disabled={
                !timeInLieuHours || timeInLieuHours < timeInLieuHoursUsed
              }
              onChange={handleTimeInLieuDateChange}
            />

            <TextField
              label="Hours"
              control={control}
              name="hoursTaken"
              onChange={(e) =>
                setValue("hoursTaken", formatHoursChange(e.target.value, 8))
              }
            />
          </>
        )}
      </Box>

      {absenceType && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                md: "repeat(1, 1fr)",
              },
              alignItems: "center",
              gap: 2,
              my: 2,
            }}
          >
            <Typography variant="caption" color="info">
              To request Time off from a future year, select a different year
            </Typography>
          </Box>

          {absenceType !== AbsenceType.timeInLieu && (
            <TimeOffDates
              control={control}
              minDate={minDate}
              maxDate={maxDate}
              maxAvailable={maxAvailable}
              minEndDate={
                getValues("startDate")
                  ? new Date(getValues("startDate"))
                  : undefined
              }
              pickerDays={pickerDays}
              handleDateChange={handleDateChange}
            />
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(1, 1fr)" },
              alignItems: "center",
              gap: 2,
            }}
          >
            <Checkbox
              required
              control={control}
              name="approvedByClient"
              label="Checked and approved by client?"
            />
            <Typography variant="caption" color="info" mb={2}>
              In case of no client, mark it as approved
            </Typography>

            <TextField
              control={control}
              name="description"
              label="Description"
              multiline
              rows={2}
              data-testid={`${TEST_ID}-description`}
              optional
              helperText="Use this field to provide additional information about your request, such as the allocation time for each day if you want to fractionate them."
            />
          </Box>
        </>
      )}
    </ModalSkeleton>
  );
};
